import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Moon, Laptop, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useTheme } from '../components/ThemeProvider';
import Header from '../components/Header';
import { toast } from 'sonner';
import { getSettings, saveSettings, DEFAULT_SETTINGS, ALL_EVENTS, getEventDefaultsForLevel, isEventAvailableForLevel } from '../utils/settings';
import {
  SPORT_TYPES,
  SWIM_SENIOR_BASELINES,
  SWIM_YOUTH_BASELINES,
  XC_BASELINES,
  TRANSITION_KEYS,
  DEFAULT_SPORT_PROFILE_KEY,
  getSportLabel,
  secondsToMMSS,
  mmssToSeconds,
} from '../utils/sportConfig';

// Group events by category for display
const EVENT_CATEGORIES = [
  { 
    id: 'sprints', 
    name: 'Sprints', 
    events: ALL_EVENTS.filter(e => e.type === 'sprint')
  },
  { 
    id: 'hurdles', 
    name: 'Hurdles', 
    events: ALL_EVENTS.filter(e => e.type === 'hurdles')
  },
  { 
    id: 'distance', 
    name: 'Distance', 
    events: ALL_EVENTS.filter(e => e.type === 'distance')
  },
  { 
    id: 'relays', 
    name: 'Relays', 
    events: ALL_EVENTS.filter(e => e.type === 'relay')
  },
  { 
    id: 'field', 
    name: 'Field Events', 
    events: ALL_EVENTS.filter(e => e.type === 'field')
  },
];

// Duration options for the dropdown (mm:ss format)
const DURATION_OPTIONS = [
  '01:00', '01:30', '02:00', '02:30', '03:00', '03:30', '04:00', '04:30', '05:00',
  '05:30', '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00',
  '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', 
  '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00',
  '20:30', '21:00', '21:30', '22:00', '22:30', '23:00', '23:30', '24:00', '24:30', '25:00', 
  '29:30', '30:00', '35:00', '39:30', '40:00', '44:30', '45:00', '50:00', '55:00', '59:30', '60:00'
];

// Single event row component
function EventSettingRow({ event, settings, onUpdate, selectedLevel }) {
  const heatsKey = `${event.id}_heats_${selectedLevel}`;
  const durationKey = `${event.id}_duration_${selectedLevel}`;
  const isFieldEvent = event.type === 'field';
  
  // Get level-specific defaults
  const levelDefaults = getEventDefaultsForLevel(event.id, selectedLevel);
  
  // If event not available for this level, don't render
  if (!levelDefaults) {
    return null;
  }
  
  // Get current values or use level defaults
  const currentHeats = settings[heatsKey] ?? levelDefaults.heats;
  const currentDuration = settings[durationKey] ?? levelDefaults.duration;
  const defaultDuration = levelDefaults.duration;
  const defaultHeats = levelDefaults.heats;
  
  // Check if current values are defaults
  const isHeatsDefault = currentHeats === defaultHeats;
  const isDurationDefault = currentDuration === defaultDuration;
  
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-foreground">{event.name}</span>
      </div>
      <div className="flex items-center gap-2">
        {/* Heats/Flights */}
        <div className="flex items-center gap-1">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">
            {isFieldEvent ? 'Flights:' : 'Heats:'}
          </Label>
          <Select 
            value={String(currentHeats)} 
            onValueChange={(val) => onUpdate(heatsKey, parseInt(val))}
          >
            <SelectTrigger className="w-14 h-8 text-xs">
              <span className="flex items-center gap-0.5">
                <SelectValue />
                {isHeatsDefault && <span className="text-muted-foreground">*</span>}
              </span>
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(n => (
                <SelectItem key={n} value={String(n)}>
                  {n === defaultHeats ? `${n}*` : n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Duration */}
        <div className="flex items-center gap-1">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">Dur:</Label>
          <Select 
            value={currentDuration} 
            onValueChange={(val) => onUpdate(durationKey, val)}
          >
            <SelectTrigger className="w-20 h-8 text-xs">
              <span className="flex items-center gap-0.5">
                <SelectValue />
                {isDurationDefault && <span className="text-muted-foreground">*</span>}
              </span>
            </SelectTrigger>
            <SelectContent>
              {DURATION_OPTIONS.map(duration => (
                <SelectItem key={duration} value={duration}>
                  {duration === defaultDuration ? `${duration}*` : duration}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

// Collapsible category component
function EventCategory({ category, settings, onUpdate, defaultExpanded = false, selectedLevel }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isFieldEvents = category.id === 'field';
  
  // Count available events for this level
  const availableEvents = category.events.filter(e => isEventAvailableForLevel(e.id, selectedLevel));
  const eventCount = availableEvents.length;
  
  // Don't show category if no events available
  if (eventCount === 0 && !isFieldEvents) {
    return null;
  }
  
  return (
    <div className={`border border-border rounded-lg overflow-hidden ${isFieldEvents ? 'opacity-60' : ''}`}>
      <button
        onClick={() => !isFieldEvents && setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-4 bg-muted/30 transition-colors ${isFieldEvents ? 'cursor-default' : 'hover:bg-muted/50'}`}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{category.name}</span>
          {isFieldEvents && (
            <Badge variant="secondary" className="bg-gray-100 text-gray-600 border-gray-300 text-xs">
              Future
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{eventCount} events</span>
          {!isFieldEvents && (
            expanded ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )
          )}
        </div>
      </button>
      {expanded && !isFieldEvents && (
        <div className="p-4 pt-0">
          {category.events.map(event => (
            <EventSettingRow 
              key={event.id} 
              event={event} 
              settings={settings} 
              onUpdate={onUpdate}
              selectedLevel={selectedLevel}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Standard set of transition-time options for "Time Between Events"
// dropdowns. Stored as fractional minutes ('0', '0.5', '1', …) for
// backwards compatibility with the schedule engine.
const TRANSITION_TIME_OPTIONS = [
  { value: '0',   label: '0:00 (default)' },
  { value: '0.5', label: '0:30' },
  { value: '1',   label: '1:00' },
  { value: '1.5', label: '1:30' },
  { value: '2',   label: '2:00' },
  { value: '2.5', label: '2:30' },
  { value: '3',   label: '3:00' },
  { value: '4',   label: '4:00' },
  { value: '5',   label: '5:00' },
  { value: '7',   label: '7:00' },
  { value: '10',  label: '10:00' },
];

/**
 * MMSSDurationInput — uncontrolled text input that displays an
 * integer-seconds value as `mm:ss` and parses user-typed `mm:ss`
 * strings back to seconds on blur / Enter.
 *
 * Uses an uncontrolled `defaultValue` + `key={value}` remount so the
 * displayed text stays in sync with the parent's value without
 * needing a useEffect-driven state mirror. The key forces React to
 * recreate the underlying <input> whenever the saved value changes
 * (e.g. tab switch, settings load) — at which point `defaultValue`
 * seeds the fresh element with the new mm:ss string. Storage is
 * unchanged (integer seconds in localStorage).
 *
 * Invalid input reverts silently — no toast spam.
 */
function MMSSDurationInput({ value, onChange, ariaLabel, dataTestId }) {
  const handleBlur = (e) => {
    const raw = e.target.value;
    const secs = mmssToSeconds(raw);
    if (secs !== null && secs >= 30) {
      // Valid input — push the parsed seconds upward. The parent will
      // re-render with the new `value`, the `key={value}` below will
      // change, and React will remount the input with a canonical
      // mm:ss `defaultValue` (so "2:5" gets normalized to "02:05").
      if (secs !== value) {
        onChange(secs);
      } else {
        // Same value but possibly different formatting — overwrite the
        // input imperatively so the user sees the canonical form.
        e.target.value = secondsToMMSS(secs);
      }
    } else {
      // Invalid input — silently snap back to the last saved value.
      e.target.value = secondsToMMSS(value);
    }
  };

  return (
    <Input
      key={value}
      type="text"
      inputMode="numeric"
      defaultValue={secondsToMMSS(value)}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        }
      }}
      placeholder="mm:ss"
      className="h-9 text-sm text-center"
      aria-label={ariaLabel}
      data-testid={dataTestId}
    />
  );
}

/**
 * BaselineRow — one editable row inside a Swimming or Cross Country
 * sub-section. Each row exposes a Heats/Races input and a Duration
 * (mm:ss) input. Keys are `${row.id}_heats` and `${row.id}_duration`
 * in the flat settings dict; duration is still stored as integer
 * seconds — only the display layer changed (Feb 2026).
 *
 * `vocab` is { heat: 'Heat' | 'Race', heats: 'Heats' | 'Races' } so
 * the XC tab can render "Races" without duplicating this row.
 */
function BaselineRow({ row, settings, onUpdate, vocab }) {
  const heatsKey = `${row.id}_heats`;
  const durationKey = `${row.id}_duration`;
  const heats = settings[heatsKey] ?? row.default_heats;
  const duration = settings[durationKey] ?? row.default_duration_seconds;
  return (
    <div className="grid grid-cols-[1fr_80px_90px] gap-2 items-center" data-testid={`baseline-row-${row.id}`}>
      <Label className="text-sm font-normal text-foreground truncate" title={row.label}>{row.label}</Label>
      <Input
        type="number"
        min={1}
        max={20}
        value={heats}
        onChange={(e) => onUpdate(heatsKey, Math.max(1, parseInt(e.target.value, 10) || 1))}
        className="h-9 text-sm text-center"
        data-testid={`baseline-${row.id}-heats`}
        aria-label={`${row.label} ${vocab.heats}`}
      />
      <MMSSDurationInput
        value={duration}
        onChange={(secs) => onUpdate(durationKey, secs)}
        ariaLabel={`${row.label} duration in minutes and seconds`}
        dataTestId={`baseline-${row.id}-duration`}
      />
    </div>
  );
}

/**
 * TransitionTimeInput — per-sport / per-environment transition gap.
 * One instance per sport tab; track has two (indoor + outdoor) because
 * the existing legacy schedule keys split that way.
 */
function TransitionTimeInput({ id, label, settingKey, helperText, settings, onUpdate }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select value={settings[settingKey] || '0'} onValueChange={(val) => onUpdate(settingKey, val)}>
        <SelectTrigger id={id} data-testid={`${id}-trigger`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TRANSITION_TIME_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
    </div>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  
  // Load settings from localStorage
  const [settings, setSettings] = useState(() => getSettings());
  const [selectedLevel, setSelectedLevel] = useState('high'); // Default to high school
  // Multi-sport timings tab — defaults to Track to keep the existing
  // first-impression for legacy users untouched. Tab state is purely UI
  // (no persistence) since each tab edits its own keys.
  const [timingsSportTab, setTimingsSportTab] = useState('track');

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header showBack={true} title="Settings" />

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Theme Selection */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Appearance</h2>
            <div className="space-y-4">
              <Label className="text-sm text-muted-foreground">Theme</Label>
              <div className="grid grid-cols-3 gap-3">
                <Button
                  variant={theme === 'light' ? 'default' : 'outline'}
                  className="flex flex-col items-center gap-2 h-auto py-4"
                  onClick={() => setTheme('light')}
                >
                  <Sun className="w-5 h-5" />
                  <span className="text-sm">Light</span>
                </Button>
                <Button
                  variant={theme === 'dark' ? 'default' : 'outline'}
                  className="flex flex-col items-center gap-2 h-auto py-4"
                  onClick={() => setTheme('dark')}
                >
                  <Moon className="w-5 h-5" />
                  <span className="text-sm">Dark</span>
                </Button>
                <Button
                  variant={theme === 'system' ? 'default' : 'outline'}
                  className="flex flex-col items-center gap-2 h-auto py-4"
                  onClick={() => setTheme('system')}
                >
                  <Laptop className="w-5 h-5" />
                  <span className="text-sm">System</span>
                </Button>
              </div>
            </div>
          </Card>

          {/* Default Sport Profile — dictates the initial sport_type
              when the coach opens the meet-creation wizard. Stored as
              `defaultSportProfile` in localStorage and consumed by
              CreateMeetFlow DEFAULT_FORM. */}
          <Card className="p-6" data-testid="default-sport-profile-card">
            <h2 className="text-lg font-semibold text-foreground mb-2">Default Sport Profile</h2>
            <p className="text-sm text-muted-foreground mb-4">
              The sport pre-selected when you create a new meet. You can
              still switch sports per meet in the wizard.
            </p>
            <Select
              value={settings[DEFAULT_SPORT_PROFILE_KEY] || 'track'}
              onValueChange={(val) => updateSetting(DEFAULT_SPORT_PROFILE_KEY, val)}
            >
              <SelectTrigger data-testid="default-sport-profile-trigger" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPORT_TYPES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`default-sport-profile-option-${s}`}>
                    {getSportLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>

          {/* Default Timings — tabbed per sport because transition
              logistics + pacing baselines diverge sharply. Track tab
              holds the legacy indoor/outdoor split (keys unchanged so
              the schedule engine load path is untouched). Swim and XC
              tabs introduce new keys defined in sportConfig.js. */}
          <Card className="p-6" data-testid="default-timings-card">
            <h2 className="text-lg font-semibold text-foreground mb-2">Default Timings</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Pacing + transition baselines used to seed new meets and
              feed the schedule prediction engine.
            </p>
            <Tabs value={timingsSportTab} onValueChange={setTimingsSportTab}>
              <TabsList className="grid grid-cols-3 mb-6" data-testid="default-timings-tabs">
                <TabsTrigger value="track" data-testid="timings-tab-track">Track &amp; Field</TabsTrigger>
                <TabsTrigger value="swim" data-testid="timings-tab-swim">Swimming</TabsTrigger>
                <TabsTrigger value="xc" data-testid="timings-tab-xc">Cross Country</TabsTrigger>
              </TabsList>

              {/* TRACK — legacy indoor/outdoor split, unchanged keys. */}
              <TabsContent value="track" className="space-y-6">
                <TransitionTimeInput
                  id="time-between-indoor"
                  label="Time Between Events (Indoor)"
                  settingKey={TRANSITION_KEYS.track_indoor}
                  helperText="Buffer time between consecutive events for indoor meets"
                  settings={settings}
                  onUpdate={updateSetting}
                />
                <TransitionTimeInput
                  id="time-between-outdoor"
                  label="Time Between Events (Outdoor)"
                  settingKey={TRANSITION_KEYS.track_outdoor}
                  helperText="Buffer time between consecutive events for outdoor meets"
                  settings={settings}
                  onUpdate={updateSetting}
                />
              </TabsContent>

              {/* SWIMMING — two sub-groups: Senior/HS vs Youth/Summer. */}
              <TabsContent value="swim" className="space-y-6" data-testid="timings-pane-swim">
                <div className="space-y-3" data-testid="swim-senior-section">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Senior / High School Pacing</h3>
                    <p className="text-xs text-muted-foreground">
                      Dual-meet defaults — high school + collegiate.
                    </p>
                  </div>
                  <div className="grid grid-cols-[1fr_80px_90px] gap-2 items-center text-[11px] uppercase tracking-wide text-muted-foreground">
                    <span>Event</span>
                    <span>Heats</span>
                    <span>Duration</span>
                  </div>
                  {SWIM_SENIOR_BASELINES.map((row) => (
                    <BaselineRow
                      key={row.id}
                      row={row}
                      settings={settings}
                      onUpdate={updateSetting}
                      vocab={{ heat: 'Heat', heats: 'Heats' }}
                    />
                  ))}
                </div>

                <Separator />

                <div className="space-y-3" data-testid="swim-youth-section">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Youth / Summer Club Pacing</h3>
                    <p className="text-xs text-muted-foreground">
                      Age-group meets — generous durations to absorb
                      slow transitions and &quot;No Time&quot; seeds.
                    </p>
                  </div>
                  <div className="grid grid-cols-[1fr_80px_90px] gap-2 items-center text-[11px] uppercase tracking-wide text-muted-foreground">
                    <span>Event</span>
                    <span>Heats</span>
                    <span>Duration</span>
                  </div>
                  {SWIM_YOUTH_BASELINES.map((row) => (
                    <BaselineRow
                      key={row.id}
                      row={row}
                      settings={settings}
                      onUpdate={updateSetting}
                      vocab={{ heat: 'Heat', heats: 'Heats' }}
                    />
                  ))}
                </div>

                <Separator />

                <TransitionTimeInput
                  id="time-between-swim"
                  label="Transition Time between Events (Swim)"
                  settingKey={TRANSITION_KEYS.swim}
                  helperText="Buffer time between heats for swim meets — typically minimal in continuously-flowing pool sessions."
                  settings={settings}
                  onUpdate={updateSetting}
                />
              </TabsContent>

              {/* CROSS COUNTRY — vocabulary swaps Heats → Races. */}
              <TabsContent value="xc" className="space-y-6" data-testid="timings-pane-xc">
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Default Number of Races / Divisions</h3>
                    <p className="text-xs text-muted-foreground">
                      Default Race Duration per division. Most XC meets
                      run a single race per division — the &quot;Races&quot;
                      column maps directly to <code>default_heats</code>
                      under the hood.
                    </p>
                  </div>
                  <div className="grid grid-cols-[1fr_80px_90px] gap-2 items-center text-[11px] uppercase tracking-wide text-muted-foreground">
                    <span>Division</span>
                    <span>Races</span>
                    <span>Duration</span>
                  </div>
                  {XC_BASELINES.map((row) => (
                    <BaselineRow
                      key={row.id}
                      row={row}
                      settings={settings}
                      onUpdate={updateSetting}
                      vocab={{ heat: 'Race', heats: 'Races' }}
                    />
                  ))}
                </div>

                <Separator />

                <TransitionTimeInput
                  id="time-between-xc"
                  label="Transition Time between Events (Cross Country)"
                  settingKey={TRANSITION_KEYS.xc}
                  helperText="Buffer time between races — XC course resets, course re-flagging, and award ceremonies usually push this to 5+ minutes."
                  settings={settings}
                  onUpdate={updateSetting}
                />
              </TabsContent>
            </Tabs>
          </Card>

          {/* Track-specific cards (Default Event Settings + Regional
              Event Preferences) — only render when the Default Timings
              tabs are on Track. Swim and XC tabs hide these to avoid
              cluttering the screen with sport-irrelevant defaults. */}
          {timingsSportTab === 'track' && (
            <>
              {/* Default Event Settings - Individual Events */}
              <Card className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Default Event Settings</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Configure default number of heats and duration for each event by school level.
            </p>
            
            {/* Legend for default indicator */}
            <div className="flex items-center gap-2 mb-4 p-2 bg-muted/30 rounded-md border border-border">
              <span className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">*</span> = app&apos;s recommended default
              </span>
            </div>
            
            {/* Level Selector */}
            <div className="mb-6">
              <Label className="mb-2 block">School Level</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={selectedLevel === 'middle' ? 'default' : 'outline'}
                  onClick={() => setSelectedLevel('middle')}
                >
                  Middle School
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={selectedLevel === 'high' ? 'default' : 'outline'}
                  onClick={() => setSelectedLevel('high')}
                >
                  High School
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={selectedLevel === 'college' ? 'default' : 'outline'}
                  onClick={() => setSelectedLevel('college')}
                  className="hidden"
                >
                  College
                </Button>
              </div>
            </div>
            
            <div className="space-y-3">
              {EVENT_CATEGORIES.map((category) => (
                <EventCategory 
                  key={category.id} 
                  category={category} 
                  settings={settings} 
                  onUpdate={updateSetting}
                  defaultExpanded={false}
                  selectedLevel={selectedLevel}
                />
              ))}
            </div>
          </Card>

          {/* Regional Event Preferences */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Regional Event Preferences</h2>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label>Short Sprint Distance</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Select the standard short sprint distance for your region
                </p>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={settings.shortSprintDistance === '55m' ? 'default' : 'outline'}
                    onClick={() => updateSetting('shortSprintDistance', '55m')}
                    className="flex-1"
                  >
                    55m Dash
                  </Button>
                  <Button
                    type="button"
                    variant={settings.shortSprintDistance === '60m' ? 'default' : 'outline'}
                    onClick={() => updateSetting('shortSprintDistance', '60m')}
                    className="flex-1"
                  >
                    60m Dash
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Short Hurdles Distance</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Select the standard short hurdles distance for your region
                </p>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={settings.shortHurdlesDistance === '55m' ? 'default' : 'outline'}
                    onClick={() => updateSetting('shortHurdlesDistance', '55m')}
                    className="flex-1"
                  >
                    55m Hurdles
                  </Button>
                  <Button
                    type="button"
                    variant={settings.shortHurdlesDistance === '60m' ? 'default' : 'outline'}
                    onClick={() => updateSetting('shortHurdlesDistance', '60m')}
                    className="flex-1"
                  >
                    60m Hurdles
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Middle Distance Run</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Select the standard middle distance for your region
                </p>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={settings.middleDistanceRun === '1500m' ? 'default' : 'outline'}
                    onClick={() => updateSetting('middleDistanceRun', '1500m')}
                    className="flex-1"
                  >
                    1500m Run
                  </Button>
                  <Button
                    type="button"
                    variant={settings.middleDistanceRun === '1600m' ? 'default' : 'outline'}
                    onClick={() => updateSetting('middleDistanceRun', '1600m')}
                    className="flex-1"
                  >
                    1600m Run
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Long Hurdles Distance</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Select the standard long hurdles distance for your region
                </p>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={settings.longHurdlesDistance === '300m' ? 'default' : 'outline'}
                    onClick={() => updateSetting('longHurdlesDistance', '300m')}
                    className="flex-1"
                  >
                    300m Hurdles
                  </Button>
                  <Button
                    type="button"
                    variant={settings.longHurdlesDistance === '400m' ? 'default' : 'outline'}
                    onClick={() => updateSetting('longHurdlesDistance', '400m')}
                    className="flex-1"
                  >
                    400m Hurdles
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <Label>Optional Events</Label>
                <p className="text-xs text-muted-foreground">
                  Select optional events to include in meet schedules by default
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="shuttle-hurdle" className="font-normal">Shuttle Hurdle Relay</Label>
                    <Switch 
                      id="shuttle-hurdle"
                      checked={settings.shuttleHurdleRelay}
                      onCheckedChange={(val) => updateSetting('shuttleHurdleRelay', val)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="race-walk-1500" className="font-normal">1500m Race Walk</Label>
                    <Switch 
                      id="race-walk-1500"
                      checked={settings.raceWalk1500m}
                      onCheckedChange={(val) => updateSetting('raceWalk1500m', val)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="race-walk-3000" className="font-normal">3000m Race Walk</Label>
                    <Switch 
                      id="race-walk-3000"
                      checked={settings.raceWalk3000m}
                      onCheckedChange={(val) => updateSetting('raceWalk3000m', val)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="race-walk-5000" className="font-normal">5000m Race Walk</Label>
                    <Switch 
                      id="race-walk-5000"
                      checked={settings.raceWalk5000m}
                      onCheckedChange={(val) => updateSetting('raceWalk5000m', val)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="sprint-medley" className="font-normal">Sprint Medley Relay</Label>
                    <Switch 
                      id="sprint-medley"
                      checked={settings.sprintMedleyRelay}
                      onCheckedChange={(val) => updateSetting('sprintMedleyRelay', val)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="distance-medley" className="font-normal">Distance Medley Relay</Label>
                    <Switch 
                      id="distance-medley"
                      checked={settings.distanceMedleyRelay}
                      onCheckedChange={(val) => updateSetting('distanceMedleyRelay', val)}
                    />
                  </div>
                </div>
              </div>
            </div>
            </Card>
            </>
          )}

          {/* Push Notifications - Future Feature */}
          <Card className="p-4 opacity-60">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Push Notifications</h2>
              <Badge variant="secondary" className="bg-gray-100 text-gray-600 border-gray-300 text-xs">
                Future
              </Badge>
            </div>
          </Card>

          {/* Support Section */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Support</h2>
            <div className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => window.open('https://foresightpulse.com', '_blank')}
                data-testid="app-tutorials-btn"
              >
                App Tutorials
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => window.open('https://foresightpulse.com', '_blank')}
                data-testid="privacy-policy-btn"
              >
                Privacy Policy
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => window.open('https://foresightpulse.com', '_blank')}
                data-testid="terms-of-service-btn"
              >
                Terms of Service
              </Button>
            </div>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end pb-8">
            <Button 
              size="lg"
              onClick={() => {
                // Save settings to localStorage
                if (saveSettings(settings)) {
                  toast.success('Settings saved successfully!');
                  navigate('/');
                } else {
                  toast.error('Failed to save settings');
                }
              }}
            >
              Save Settings
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
